import { CreateAndAssignGoalConfigurationInput, GoalConfigurationTemplateTableItemFragment } from "apps/web/src/types";
import { z } from "zod";
import { IntegerSchema } from "@notemeal/validators";
import { ORDERED_DAYS_OF_WEEK } from "@notemeal/utils-date-time";
import { GoalConfigurationDeepPartial, GoalConfigurationFormType } from "../Athlete/GoalTracker/GoalConfiguration/GoalConfigurationSchema";
import { AssignGoalAdvancedSelectionState } from "./useAdvancedSelection";
import { serializeDate } from "@notemeal/utils-date-time";
import { buildAdvancedSelectionInput } from "../Tags/reducers/advancedSelectionReducers";

export enum CreateGoalConfigurationTemplateDialogMode {
    Create = "create",
    Edit = "edit",
    Duplicate = "duplicate",
    View = "view"
}

export const GoalCategorySchema = z.object(
    {
        id: z.string().min(1),
        name: z.string().min(1),
        defaultEmoji: z.string().min(1),
    },
    { required_error: "Required" }
);

export const CompletionCriteriaSchema = z.object({
    markAsComplete: z.boolean({ required_error: "Please select one of the choices below" }),
    target: z.object({ value: IntegerSchema, unit: z.string({ required_error: "Required" }).min(1, { message: "Required" }) }).optional(),
});

export const GoalConfigurationTemplateFormSchema = z.object({
    task: z.string({ required_error: "Task is required" }).min(1, { message: "Required" }).max(40, { message: "Max 40 characters" }),
    type: GoalCategorySchema,
    completionCriteria: CompletionCriteriaSchema,
});

export type GoalConfigurationTemplateFormType = z.infer<typeof GoalConfigurationTemplateFormSchema>;

export const goalConfigurationFormDefaultValues = (goalConfiguration: GoalConfigurationTemplateTableItemFragment | null) => {
    if (goalConfiguration) {
        const { category, target } = goalConfiguration;
        return {
            task: goalConfiguration.name,
            type: {
                id: category.id,
                name: category.name,
                defaultEmoji: category.defaultEmoji,
            },
            completionCriteria: {
                markAsComplete: !target,
                target: target ? { value: target.value, unit: target.unit } : undefined,
            }
        };
    }
    return {
        task: "",
        type: undefined,
        completionCriteria: { markAsComplete: undefined },
    };
};

export const assignGoalConfigurationTemplateFormDefaultValues = (goalConfiguration: GoalConfigurationTemplateTableItemFragment): GoalConfigurationDeepPartial => {
    const { name, category, target } = goalConfiguration;
    const today = new Date();
    return {
        task: name,
        type: {
            id: category.id,
            name: category.name,
            defaultEmoji: category.defaultEmoji,
        },
        completionCriteria: {
            markAsComplete: !target,
            target: target ? { value: target.value, unit: target.unit } : undefined,
        },
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        assignments: { type: "weekly", daysOfWeek: [...ORDERED_DAYS_OF_WEEK], startDate: today, endDate: undefined },
        reminders: [],
    };
};

export const createAndAssignGoalConfigurationInput = (goal: GoalConfigurationFormType, goalConfigTemplate: GoalConfigurationTemplateTableItemFragment, advancedSelectState: AssignGoalAdvancedSelectionState): CreateAndAssignGoalConfigurationInput => {
    const teamIds = advancedSelectState.__typename === "Teams" ? advancedSelectState.teams.map(({ id }) => id) : null;
    const advancedSelectionInput = advancedSelectState.__typename === "Tags" ? buildAdvancedSelectionInput(advancedSelectState.advancedSelectionState) : null;

    return {
        goalConfigurationTemplateId: goalConfigTemplate.id,
        timezone: goal.timezone,
        advancedSelectionInput,
        teamIds,
        reminders: goal.reminders,
        individualDates: goal.assignments.type === "individualDates" ? goal.assignments.dates : [],
        daysOfWeek: goal.assignments.type === "weekly" ? {
            dayOfWeekAssignments: goal.assignments.daysOfWeek,
            startDate: serializeDate(goal.assignments.startDate),
            endDate: serializeDate(goal.assignments.endDate),
        } : null
    }
}

export const buildEditGoalInput = (
    goal: GoalConfigurationTemplateFormType,
    selectedGoal: GoalConfigurationTemplateTableItemFragment
) => {
    return {
        id: selectedGoal.id,
        name: goal.task,
        categoryId: goal.type.id,
        emoji: goal.type.defaultEmoji,
        targetId: selectedGoal?.target?.id || null,
        target: goal.completionCriteria.markAsComplete
            ? null
            : {
                value: goal.completionCriteria.target?.value || 0,
                unit: goal.completionCriteria.target?.unit || ''
            }
    };
};

export const buildCreateGoalInput = (goal: GoalConfigurationTemplateFormType) => {
    return {
        name: goal.task,
        categoryId: goal.type.id,
        emoji: goal.type.defaultEmoji,
        target: goal.completionCriteria.markAsComplete
            ? null
            : {
                value: goal.completionCriteria.target?.value || 0,
                unit: goal.completionCriteria.target?.unit || ''
            }
    };
};
